/**
 * Account actions — the remaining legacy operations, grouped by how much damage
 * they do. The one-click ones sit together; anything irreversible is in the
 * danger zone behind a typed confirmation.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  BadgeCheck,
  Briefcase,
  FileCheck,
  Loader2,
  MailX,
  Trash2,
} from "lucide-react";
import {
  BILLING_PROVIDER_LABELS,
  type ChangeEmailResponse,
  type UserAccount,
} from "../../../../../shared/index";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, ConfirmDialog } from "./parts";
import { describeError } from "./format";

type Destructive = "stopEmails" | "deleteUser";

const DESTRUCTIVE_COPY: Record<
  Destructive,
  { title: string; body: string; confirmLabel: string; typed: boolean }
> = {
  stopEmails: {
    title: "Stop all emails",
    body: "The user will be unsubscribed from every email notification. They can re-subscribe from their settings.",
    confirmLabel: "Stop all emails",
    typed: false,
  },
  deleteUser: {
    title: "Delete user data",
    body: "This permanently deletes the user's data upstream. It cannot be undone from this panel.",
    confirmLabel: "Delete user data",
    typed: true,
  },
};

export function DangerTab({ account }: { account: UserAccount }) {
  const queryClient = useQueryClient();
  const email = account.email;
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["site-user", email] });

  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmEmailChange, setConfirmEmailChange] = useState(false);
  const [updateProviders, setUpdateProviders] = useState(false);
  const [pendingDestructive, setPendingDestructive] =
    useState<Destructive | null>(null);

  // Which provider accounts exist under this user's addresses — needed to say
  // exactly what an email change would (or would not) touch. Shares the Billing
  // tab's cache, so opening the dialog rarely costs a request.
  const billing = useQuery({
    queryKey: ["site-user-billing", email],
    queryFn: () => api.siteUsers.billing(email),
    enabled: confirmEmailChange,
    retry: false,
  });

  const providerAccounts = (billing.data?.snapshots ?? []).flatMap((s) =>
    s.customers.map((c) => ({
      provider: BILLING_PROVIDER_LABELS[s.provider],
      ...c,
    }))
  );

  const run = useMutation({
    mutationFn: (action: () => Promise<{ message: string }>) => action(),
    onSuccess: (r) => {
      toast.success(r.message);
      for (const u of (r as ChangeEmailResponse).providerUpdates ?? []) {
        const who = `${BILLING_PROVIDER_LABELS[u.provider]} ${u.customerId}`;
        if (u.ok) toast.success(`${who} updated`);
        else toast.error(`${who} failed: ${u.error}`);
      }
      setConfirmEmailChange(false);
      setPendingDestructive(null);
      setUpdateProviders(false);
      setNewEmail("");
      setPassword("");
      refresh();
      queryClient.invalidateQueries({ queryKey: ["site-user-billing"] });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const busy = run.isPending;
  const destructive = pendingDestructive
    ? DESTRUCTIVE_COPY[pendingDestructive]
    : null;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Change email" description="Moves the account to a new address">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newEmail">New email</Label>
            <Input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new@example.com"
            />
          </div>
          <Button
            className="w-full"
            disabled={!newEmail.includes("@") || busy}
            onClick={() => setConfirmEmailChange(true)}
          >
            Change email
          </Button>
        </div>
      </Card>

      <Card title="Set password" description="Sets a new password directly">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword">New password</Label>
            <Input
              id="newPassword"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Generated or agreed with the user"
            />
          </div>
          <Button
            className="w-full"
            disabled={password.length < 6 || busy}
            onClick={() =>
              run.mutate(() => api.siteUsers.updatePassword({ email, password }))
            }
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Set password
          </Button>
        </div>
      </Card>

      <Card
        title="One-click actions"
        description="Safe to re-run; each acts on this account"
        className="lg:col-span-2"
      >
        <div className="grid gap-2 sm:grid-cols-3">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => run.mutate(() => api.siteUsers.verifyEmail(email))}
          >
            <BadgeCheck className="mr-2 h-4 w-4" />
            Verify email
          </Button>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() =>
              run.mutate(() => api.siteUsers.activateTermsOfUse(email))
            }
          >
            <FileCheck className="mr-2 h-4 w-4" />
            Activate terms of use
          </Button>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => run.mutate(() => api.siteUsers.addPortfolio(email))}
          >
            <Briefcase className="mr-2 h-4 w-4" />
            Add Nasdaq portfolio
          </Button>
        </div>
      </Card>

      <Card
        title="Danger zone"
        description="These are hard or impossible to undo"
        className="border-destructive/40 lg:col-span-2"
      >
        <div className="grid gap-2 sm:grid-cols-2">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => setPendingDestructive("stopEmails")}
          >
            <MailX className="mr-2 h-4 w-4" />
            Stop all emails
          </Button>
          <Button
            variant="destructive"
            disabled={busy}
            onClick={() => setPendingDestructive("deleteUser")}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete user data
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmEmailChange}
        onOpenChange={setConfirmEmailChange}
        title="Change email"
        description={
          <>
            <p>
              <span className="font-medium">{email}</span> becomes{" "}
              <span className="font-medium">{newEmail}</span>. The user signs in
              with the new address from then on.
            </p>

            {billing.isLoading && (
              <p className="text-muted-foreground">
                Checking the payment providers…
              </p>
            )}

            {providerAccounts.length > 0 && (
              <div className="rounded-lg border p-3">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={updateProviders}
                    onChange={(e) => setUpdateProviders(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">
                      Also update the email at the payment provider
                      {providerAccounts.length > 1 ? "s" : ""}
                    </span>
                    <ul className="mt-1 text-xs text-muted-foreground">
                      {providerAccounts.map((p) => (
                        <li key={`${p.provider}-${p.customerId}`}>
                          {p.provider} · {p.customerId} — currently {p.email}
                        </li>
                      ))}
                    </ul>
                  </span>
                </label>
              </div>
            )}

            {providerAccounts.length > 0 && !updateProviders && (
              <p className="flex items-start gap-2 rounded-lg border border-warning/50 bg-warning/10 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  You are about to change this user's email{" "}
                  <span className="font-medium">
                    without changing it at the payment provider
                    {providerAccounts.length > 1 ? "s" : ""}
                  </span>
                  . Receipts and failed-payment emails will keep going to{" "}
                  {providerAccounts.map((p) => p.email).join(", ")}.
                </span>
              </p>
            )}

            {providerAccounts.length === 0 && !billing.isLoading && (
              <p className="text-muted-foreground">
                No payment-provider account matched this user, so there is
                nothing to update there.
              </p>
            )}
          </>
        }
        confirmLabel={updateProviders ? "Change email everywhere" : "Change email"}
        pending={busy}
        onConfirm={() =>
          run.mutate(() =>
            api.siteUsers.changeEmail({ email, newEmail, updateProviders })
          )
        }
      />

      <ConfirmDialog
        open={pendingDestructive !== null}
        onOpenChange={(open) => !open && setPendingDestructive(null)}
        title={destructive?.title ?? ""}
        description={
          <>
            <p>{destructive?.body}</p>
            <p className="text-muted-foreground">Account: {email}</p>
          </>
        }
        confirmLabel={destructive?.confirmLabel ?? "Confirm"}
        confirmText={destructive?.typed ? email : undefined}
        destructive
        pending={busy}
        onConfirm={() => {
          if (pendingDestructive === "stopEmails") {
            run.mutate(() => api.siteUsers.stopEmails(email));
          } else if (pendingDestructive === "deleteUser") {
            run.mutate(() => api.siteUsers.deleteUser(email));
          }
        }}
      />
    </div>
  );
}
